import i18n from 'i18n-literally'
import { prop, sort } from 'ramda'
import { createSelector } from 'redux-bundler'

import { addBreadcrumb, captureException } from '@sentry/capacitor'
import { normalize } from 'normalizr'
import queryString from 'query-string'

import cache from '~/src/IO/Cache'
import {
  AUTH_URL,
  clearNativeSecureSession,
  clearNativeSession,
  clearTokens,
  getTokens,
  persistNativeBiometricTokens,
  refreshTokens,
  setNativeBiometricSessionTokens,
  setTokens,
} from '~/src/Lib/Auth'
import { doEntitiesReceived, getAsyncActionIdentifiers } from '~/src/Lib/createEntityBundle'
import getGlobal from '~/src/Lib/getGlobal'
import createLogger from '~/src/Lib/Logging'
import safeStorage from '~/src/Lib/safeStorage'
import { defer, EMPTY_OBJECT, parseApiErrors } from '~/src/Lib/Utils'
import { getNumberSort } from '~/src/Lib/Utils/arrays'
import {
  authenticateNativeBiometrics,
  checkNativeBiometrics,
  readNativeAuthUser,
  readNativeAuthTokens,
  removeNativeAuthTokens,
  writeNativeAuthTokens,
} from '~/src/Native/biometrics'
import { isNativePlatform } from '~/src/Native/platform'
import { removeNativeSessionTokens, writeNativeSessionTokens } from '~/src/Native/session'
import { isSolusOnlyDevOverride } from '~/src/Native/solus/dev'
import { DEFAULT_NEXT, REGISTER_ROUTES } from '~/src/Routes/constants'

import { REACTOR_PRIORITIES } from '../constants'
import { Facility } from '../Schemas'
import { NEXT_FACILITY_URL } from './me/currentFacility'
import { clearLSM, getLSM } from './me/utils'

const logger = createLogger('Store/auth')

const { location } = getGlobal()

const LOGIN_PATTERN = /^\/(login|switching)/
const AP_CONTACT_NEXT = '/account'
const SOLUS_LOGIN_ERROR = 'No AROYA facility found. Please use the SOLUS app to connect to your SOLUS.'

const isSolusLoginError = response =>
  (isNativePlatform() || isSolusOnlyDevOverride()) &&
  response &&
  typeof response === 'object' &&
  Array.isArray(response.nonFieldErrors) &&
  response.nonFieldErrors.includes(SOLUS_LOGIN_ERROR)

export const loginActions = getAsyncActionIdentifiers('login')
export const logoutActions = getAsyncActionIdentifiers('logout')
const CLEAR_AUTH_STATE = 'CLEAR_AUTH_STATE'
export const CLEAR_SOLUS_ONLY = 'CLEAR_SOLUS_ONLY'
const BIOMETRIC_CHECK_START = 'BIOMETRIC_CHECK_START'
const BIOMETRIC_CHECK_SUCCEED = 'BIOMETRIC_CHECK_SUCCEED'
const BIOMETRIC_CHECK_FAIL = 'BIOMETRIC_CHECK_FAIL'
const BIOMETRIC_LOGIN_START = 'BIOMETRIC_LOGIN_START'
const BIOMETRIC_LOGIN_SUCCEED = 'BIOMETRIC_LOGIN_SUCCEED'
const BIOMETRIC_LOGIN_FAIL = 'BIOMETRIC_LOGIN_FAIL'
const BIOMETRIC_ENABLE_START = 'BIOMETRIC_ENABLE_START'
const BIOMETRIC_ENABLE_SUCCEED = 'BIOMETRIC_ENABLE_SUCCEED'
const BIOMETRIC_ENABLE_FAIL = 'BIOMETRIC_ENABLE_FAIL'
const BIOMETRIC_DISABLE_START = 'BIOMETRIC_DISABLE_START'
const BIOMETRIC_DISABLE_SUCCEED = 'BIOMETRIC_DISABLE_SUCCEED'
const BIOMETRIC_DISABLE_FAIL = 'BIOMETRIC_DISABLE_FAIL'

export const forgotActions = getAsyncActionIdentifiers('forgot')
const CLEAR_FORGOT_STATE = 'CLEAR_FORGOT_STATE'

export const resetActions = getAsyncActionIdentifiers('reset')
const CLEAR_RESET_STATE = 'CLEAR_RESET_STATE'

export const validateActions = getAsyncActionIdentifiers('validate')

const SET_URL_HISTORY = 'SET_URL_HISTORY'

export const defaultState = {
  authenticated: false,
  solusOnly: false,
  loginProcessing: false,
  loginFailed: false,
  logoutProcessing: false,
  loginMessage: '',
  urlHistory: [],
  biometric: {
    availability: 'unknown',
    enabled: false,
    error: null,
    label: '',
    processing: false,
  },
  forgot: {
    loading: false,
    success: null,
    error: null,
  },
  reset: {
    loading: false,
    success: null,
    error: null,
  },
  validate: {
    loading: true,
    error: null,
    response: {
      valid: null,
      email: null,
    },
  },
}
const {
  types: { start: loginStart, succeed: loginSucceed, fail: loginFail },
} = loginActions
const {
  types: { start: logoutStart, succeed: logoutSucceed, fail: logoutFail },
} = logoutActions
const {
  types: { start: forgotStart, succeed: forgotSucceed, fail: forgotFail },
} = forgotActions
const {
  types: { start: resetStart, succeed: resetSucceed, fail: resetFail },
} = resetActions
const {
  types: { start: validateStart, succeed: validateSucceed, fail: validateFail },
} = validateActions

const completeLogin = async ({ auth, alreadyHasAccount, dispatch, secure = false }) => {
  const hasFacilities = Array.isArray(auth?.facilities) && auth.facilities.length > 0
  const solusOnly = isSolusOnlyDevOverride() || (isNativePlatform() && auth?.solusOnly === true && !hasFacilities)
  const sessionAuth = { ...auth }
  if (solusOnly) sessionAuth.solusOnly = true
  else delete sessionAuth.solusOnly
  if (secure) {
    setNativeBiometricSessionTokens(sessionAuth)
  } else {
    clearNativeSession()
    setTokens(sessionAuth)
  }
  cache.setUserToken(sessionAuth.user)
  if (alreadyHasAccount && !solusOnly) {
    dispatch({ actionCreator: 'doUpdateUrl', args: [REGISTER_ROUTES.FACILITY_INFO] })
  }

  if (solusOnly) {
    clearLSM()
    dispatch({ actionCreator: 'doCurrentFacilitySet', args: [null] })
    dispatch({ type: NEXT_FACILITY_URL, payload: null })
  } else if (Array.isArray(sessionAuth?.facilities)) {
    const { entities } = normalize(sessionAuth.facilities, [Facility])
    const { facilities } = entities
    const { facility } = getLSM()
    if (!(facility in facilities)) {
      logger.debug('facility', facility, 'not found in', facilities)
      clearLSM()
      const [defaultFacility] = sort(getNumberSort(prop('id')), sessionAuth.facilities)
      if (defaultFacility?.id) {
        dispatch({ actionCreator: 'doNextFacilitySet', args: [defaultFacility.id] })
      } else {
        logger.debug('no default facility:', sessionAuth.facilities)
      }
    }
    dispatch(doEntitiesReceived(entities, { replace: true }))
  }
  dispatch({
    type: loginSucceed,
    payload: solusOnly ? { authenticated: !!sessionAuth.user, solusOnly: true } : { authenticated: !!sessionAuth.user },
  })
  dispatch({ actionCreator: 'doFetchMe' })
  if (!solusOnly) dispatch({ actionCreator: 'doFetchSystem' })
}

export default {
  name: 'auth',
  reducer: (state = defaultState, action = EMPTY_OBJECT) => {
    if (!action || !action.type) return state
    switch (action.type) {
      case loginStart:
        return { ...state, loginProcessing: true, loginFailed: false }
      case loginFail:
        return {
          ...state,
          loginProcessing: false,
          loginFailed: true,
          loginMessage: action.error ?? action.payload?.error ?? 'Unable to login',
        }
      case loginSucceed:
        return {
          ...state,
          ...defaultState,
          biometric: state.biometric,
          authenticated: action?.payload?.authenticated ?? false,
          solusOnly: action?.payload?.solusOnly === true,
          authenticatedAt: Date.now(),
        }
      case CLEAR_SOLUS_ONLY:
        return { ...state, solusOnly: false }
      case BIOMETRIC_CHECK_START:
        return { ...state, biometric: { ...state.biometric, error: null, processing: true } }
      case BIOMETRIC_CHECK_SUCCEED:
        return {
          ...state,
          biometric: {
            ...state.biometric,
            availability: action.payload.available ? 'available' : 'unavailable',
            enabled: action.payload.enabled,
            error: null,
            label: action.payload.label,
            processing: false,
          },
        }
      case BIOMETRIC_CHECK_FAIL:
        return {
          ...state,
          biometric: {
            ...state.biometric,
            availability: 'unavailable',
            enabled: false,
            error: action.payload,
            processing: false,
          },
        }
      case BIOMETRIC_LOGIN_START:
      case BIOMETRIC_ENABLE_START:
      case BIOMETRIC_DISABLE_START:
        return { ...state, biometric: { ...state.biometric, error: null, processing: true } }
      case BIOMETRIC_LOGIN_SUCCEED:
        return { ...state, biometric: { ...state.biometric, error: null, processing: false } }
      case BIOMETRIC_ENABLE_SUCCEED:
        return { ...state, biometric: { ...state.biometric, enabled: true, error: null, processing: false } }
      case BIOMETRIC_DISABLE_SUCCEED:
        return { ...state, biometric: { ...state.biometric, enabled: false, error: null, processing: false } }
      case BIOMETRIC_LOGIN_FAIL:
      case BIOMETRIC_ENABLE_FAIL:
      case BIOMETRIC_DISABLE_FAIL:
        return { ...state, biometric: { ...state.biometric, error: action.payload, processing: false } }
      case logoutStart:
        return { ...state, logoutProcessing: true }
      case logoutFail:
        return { ...state, logoutProcessing: false }
      case logoutSucceed:
      case CLEAR_AUTH_STATE:
        return { ...defaultState }
      case forgotStart:
        return { ...state, forgot: { ...defaultState.forgot, loading: true } }
      case forgotSucceed:
        return { ...state, forgot: { ...defaultState.forgot, success: true } }
      case forgotFail:
        return {
          ...state,
          forgot: { ...defaultState.forgot, error: action.error },
        }
      case CLEAR_FORGOT_STATE:
        return { ...state, forgot: defaultState.forgot }
      case resetStart:
        return { ...state, reset: { ...defaultState.reset, loading: true } }
      case resetSucceed:
        return { ...state, reset: { ...defaultState.reset, success: true } }
      case resetFail:
        return {
          ...state,
          reset: { ...defaultState.reset, error: action.payload },
        }
      case CLEAR_RESET_STATE:
        return { ...state, reset: defaultState.reset }
      case SET_URL_HISTORY:
        return { ...state, urlHistory: [...state.urlHistory, action.payload].slice(-2) }
      case validateStart:
        return { ...state, validate: { ...defaultState.validate } }
      case validateFail:
        return {
          ...state,
          validate: { ...defaultState.validate, loading: false, error: action.payload },
        }
      case validateSucceed:
        return {
          ...state,
          validate: { ...defaultState.validate, loading: false, response: action.payload },
        }
      default:
        return state
    }
  },
  selectAuth: state => state.auth,
  selectLastUrl: state => state.auth.urlHistory.slice(-2, -1).pop() || '',
  doLogin:
    credentials =>
    async ({ apiFetch, dispatch, store }) => {
      const { types } = loginActions
      const { alreadyHasAccount } = store.selectAccountCreation()

      dispatch({ type: types.start })
      addBreadcrumb({
        category: 'auth',
        message: `Attempting to authenticate "${credentials.email}"`,
        level: 'info',
      })
      let nativeLoginStage = null
      let serverAuthenticated = false
      try {
        let auth = await apiFetch(AUTH_URL, credentials, {
          method: 'POST',
          allowedCodes: [400],
          credentials: 'include',
          authenticated: false,
        })
        let solusOnly = isSolusOnlyDevOverride()
        if (isSolusLoginError(auth)) {
          nativeLoginStage = 'solus-login-retry'
          auth = await apiFetch(AUTH_URL, credentials, {
            method: 'POST',
            allowedCodes: [400],
            credentials: 'include',
            authenticated: false,
            headers: { 'X-Client': 'SOLUS' },
          })
          solusOnly = !(auth && 'nonFieldErrors' in auth)
        }
        if (auth && 'nonFieldErrors' in auth) {
          dispatch({ type: types.fail, error: auth.nonFieldErrors[0] })
          addBreadcrumb({
            category: 'auth',
            message: `Authentication failed for "${credentials.email}"`,
            data: auth.nonFieldErrors,
            level: 'warning',
          })
        } else {
          serverAuthenticated = true
          if (solusOnly) auth = { ...auth, solusOnly: true }
          else if (auth?.solusOnly === true) {
            auth = { ...auth }
            delete auth.solusOnly
          }
          if (isNativePlatform()) {
            nativeLoginStage = 'biometric-session-reconciliation'
            const enrolledUser = await readNativeAuthUser()
            if (enrolledUser !== null && String(enrolledUser) !== String(auth.user)) {
              await removeNativeAuthTokens()
            } else if (enrolledUser !== null) {
              await writeNativeAuthTokens(auth)
            }
            nativeLoginStage = 'session-persistence'
            await writeNativeSessionTokens(auth)
          }
          nativeLoginStage = 'app-state-initialization'
          clearNativeSession()
          await completeLogin({ auth, alreadyHasAccount, dispatch })
          nativeLoginStage = null
          addBreadcrumb({
            category: 'auth',
            message: `Authenticated "${credentials.email}"`,
            level: 'info',
          })
        }
      } catch (error) {
        if (safeStorage('local').debug === 'true') console.error(error)
        const nativeSetupFailed = isNativePlatform() && serverAuthenticated
        if (nativeSetupFailed) {
          captureException(error, {
            tags: { native_login_stage: nativeLoginStage ?? 'post-authentication' },
          })
        }
        dispatch({
          type: types.fail,
          error: nativeSetupFailed
            ? i18n`Sign-in succeeded, but AROYA could not finish secure device setup. Close and reopen the app, then try again.`
            : parseApiErrors(error) || i18n`Unable to login.`,
        })
        addBreadcrumb({
          category: 'auth',
          message: `Authentication failed for "${credentials.email}"`,
          data: error?.response,
          level: 'warning',
        })
      }
    },
  doBiometricCheck:
    () =>
    async ({ dispatch }) => {
      dispatch({ type: BIOMETRIC_CHECK_START })
      try {
        const status = await checkNativeBiometrics()
        dispatch({ type: BIOMETRIC_CHECK_SUCCEED, payload: status })
      } catch {
        dispatch({ type: BIOMETRIC_CHECK_FAIL, payload: i18n`Biometric login is unavailable.` })
      }
    },
  doBiometricEnable:
    () =>
    async ({ dispatch }) => {
      dispatch({ type: BIOMETRIC_ENABLE_START })
      try {
        const tokens = getTokens()
        const status = await checkNativeBiometrics()
        if (!status.available || !tokens.access || !tokens.refresh) throw new Error('Biometric login is unavailable.')
        await authenticateNativeBiometrics()
        await persistNativeBiometricTokens(tokens)
        dispatch({ type: BIOMETRIC_ENABLE_SUCCEED })
      } catch {
        dispatch({ type: BIOMETRIC_ENABLE_FAIL, payload: i18n`Unable to enable biometric login.` })
      }
    },
  doBiometricDisable:
    () =>
    async ({ dispatch }) => {
      dispatch({ type: BIOMETRIC_DISABLE_START })
      try {
        await removeNativeAuthTokens()
        clearNativeSecureSession()
        dispatch({ type: BIOMETRIC_DISABLE_SUCCEED })
      } catch {
        dispatch({ type: BIOMETRIC_DISABLE_FAIL, payload: i18n`Unable to disable biometric login.` })
      }
    },
  doBiometricLogin:
    () =>
    async ({ dispatch, store }) => {
      dispatch({ type: BIOMETRIC_LOGIN_START })
      try {
        await authenticateNativeBiometrics()
        const tokens = await readNativeAuthTokens()
        if (!tokens) {
          await removeNativeAuthTokens()
          throw new Error('Stored biometric tokens are invalid.')
        }
        setNativeBiometricSessionTokens(tokens)
        const refreshed = await refreshTokens(store, { redirectOnFailure: false, skipPublicRoutes: false })
        if (!refreshed) throw new Error('Stored biometric tokens are expired.')
        const { alreadyHasAccount } = store.selectAccountCreation()
        await completeLogin({ auth: refreshed, alreadyHasAccount, dispatch, secure: true })
        dispatch({ type: BIOMETRIC_LOGIN_SUCCEED })
      } catch {
        clearNativeSession()
        dispatch({ type: BIOMETRIC_LOGIN_FAIL, payload: i18n`Unable to use biometric login.` })
      }
    },
  doLogout:
    (reset = false) =>
    async ({ dispatch, store }) => {
      const { types } = logoutActions
      dispatch({ type: types.start })
      let success
      try {
        store.doClearAccountToken()
        clearTokens()
        if (isNativePlatform()) await removeNativeSessionTokens()
        dispatch({ type: types.succeed })
        cache.logger.info('clearing cache')
        await cache.clear()
        success = true
      } catch (error) {
        success = false
        logger.error(error)
        dispatch({ type: types.fail, error })
      } finally {
        defer(() => {
          location.replace(`/login${reset === true ? '?reset=1' : ''}`)
        }, defer.priorities.low)
      }
      return success
    },
  doAuthReset: () => ({ type: CLEAR_AUTH_STATE }),
  doForgotPassword:
    payload =>
    async ({ apiFetch, dispatch }) => {
      const { types } = forgotActions
      dispatch({ type: types.start })
      try {
        const response = await apiFetch('/forgot/', payload, {
          method: 'POST',
          authenticated: false,
        })
        if (response.success) {
          dispatch({ type: types.succeed })
        } else {
          dispatch({ type: types.fail, error: 'No account with that email address was found.' })
        }
      } catch {
        dispatch({ type: types.fail, error: 'Unable to reset your password' })
      }
    },
  doForgotClearState:
    () =>
    async ({ dispatch }) => {
      dispatch({ type: CLEAR_FORGOT_STATE })
    },
  doResetPassword:
    payload =>
    async ({ apiFetch, dispatch, store }) => {
      const { types } = resetActions
      dispatch({ type: types.start })
      try {
        const response = await apiFetch('/reset/', payload, {
          method: 'POST',
          allowedCodes: [400],
          authenticated: false,
        })
        if ('success' in response) {
          dispatch({ type: types.succeed })
          store.doLogout(true)
        } else {
          dispatch({ type: types.fail, error: response[0] })
        }
      } catch {
        dispatch({ type: types.fail, error: 'Unable to reset your password' })
      }
    },
  doResetTokenValidate:
    payload =>
    async ({ apiFetch, dispatch }) => {
      const { types } = validateActions
      dispatch({ type: types.start })
      try {
        const response = await apiFetch('/token/validate/', payload, { authenticated: false })
        dispatch({ type: types.succeed, payload: response })
      } catch {
        dispatch({ type: types.fail, error: 'Unable to validate reset password token.' })
      }
    },
  doResetClearState:
    () =>
    async ({ dispatch }) => {
      dispatch({ type: CLEAR_RESET_STATE })
    },
  doSetUrlHistory:
    payload =>
    async ({ dispatch }) => {
      dispatch({ type: SET_URL_HISTORY, payload })
    },
  doGoBack:
    defaultUrl =>
    ({ store }) => {
      const lastUrl = store.selectLastUrl()
      store.doUpdateUrl(lastUrl || defaultUrl)
    },
  selectTokenValidationResponse: createSelector('selectAuth', auth => auth.validate),
  selectIsLoggingOut: createSelector('selectAuth', auth => !!auth.logoutProcessing),
  reactChangedUrl: createSelector('selectRouteInfo', 'selectAuth', ({ url }, { urlHistory }) => {
    if (urlHistory.slice(-1).pop() !== url) {
      return {
        actionCreator: 'doSetUrlHistory',
        args: [url],
        priority: REACTOR_PRIORITIES.HIGH,
      }
    }
    return undefined
  }),
  reactLoginSuccess: createSelector(
    'selectRouteInfo',
    'selectAuth',
    'selectMeRaw',
    'selectSystemRaw',
    'selectCurrentMembership',
    'selectCurrentFacility',
    ({ url }, { authenticated }, me, system, currentMembership, currentFacility) => {
      if (
        !me.lastSuccess ||
        !system.lastSuccess ||
        currentMembership?.facility != currentFacility?.id ||
        !currentFacility?.roles?.length
      ) {
        return undefined
      }
      if (url.match(LOGIN_PATTERN) && authenticated) {
        let { next } = queryString.parse(location.search)
        if (!next) {
          next =
            currentMembership?.isApContact && !currentFacility.roles.includes(currentMembership.role)
              ? AP_CONTACT_NEXT
              : DEFAULT_NEXT
        }
        if (next === '/login') next = DEFAULT_NEXT
        return {
          actionCreator: 'doUpdateUrl',
          args: [next],
          priority: REACTOR_PRIORITIES.HIGH,
        }
      }
      return undefined
    },
  ),
  reactForgotSuccess: createSelector('selectRouteInfo', 'selectAuth', ({ pattern }, { forgot }) => {
    if (pattern === '/forgot' && forgot.success) {
      return {
        actionCreator: 'doUpdateUrl',
        args: ['/login'],
        priority: REACTOR_PRIORITIES.HIGH,
      }
    }
    return undefined
  }),
}
